import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthService } from '../../../services/auth.service';
import { useToast } from '../../../context/ToastContext';
import { CommercialReportService } from '../../../services/commercialReport.service';
import { ExpAgingGroupType, IExpAgingBucket, IExpAgingReport } from '../../../interfaces/commercial.interface';
import { money, triggerDownload } from '../../statutoryRegisters/shared';
import { toLocalIsoDate } from '../../../utils/dateUtils';

const distinctCurrencies = (buckets: IExpAgingBucket[]): string[] => {
    const seen: string[] = [];
    buckets.forEach(b => { if (!seen.includes(b.currencyCode)) seen.push(b.currencyCode); });
    return seen;
};

const BucketTable: React.FC<{ title: string; buckets: IExpAgingBucket[]; currency: string; groupLabel: string; testIdPrefix: string }> = (
    { title, buckets, currency, groupLabel, testIdPrefix }
) => (
    <TableContainer data-help-section="commercialReports.expAging.buckets" component={Paper} sx={{ mb: 2 }} data-testid={`${testIdPrefix}-${currency}`}>
        <Typography variant="subtitle2" sx={{ p: 1 }}>{title} — {currency}</Typography>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell data-help="commercialReports.expAging.col.group">{groupLabel}</TableCell>
                    <TableCell data-help="commercialReports.expAging.col.bucket">Bucket</TableCell>
                    <TableCell data-help="commercialReports.expAging.col.count" align="right">Count</TableCell>
                    <TableCell data-help="commercialReports.expAging.col.amount" align="right">Amount</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {buckets.map((b, i) => (
                    <TableRow key={`${b.groupKey}-${b.bucketLabel}-${b.currencyCode}-${i}`}>
                        <TableCell>{b.groupKey}</TableCell>
                        <TableCell>{b.bucketLabel}</TableCell>
                        <TableCell align="right">{b.count}</TableCell>
                        <TableCell align="right">{money(b.amount)}</TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

/**
 * T11 report 2 — unrealized-EXP aging (T8 brief item 2). §4.7/T-COMM-1 caveat: buyer grouping is
 * TEXT until a real buyer-id backfill ships, so spelling variants fragment — rendered here, never
 * silently tolerated. R-2 as extended: buckets are keyed (groupType, groupKey, currencyCode,
 * bucketLabel) — one sub-total block per currency at every grouping level.
 */
const UnrealizedExpAgingReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [report, setReport] = useState<IExpAgingReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const data = await CommercialReportService.getUnrealizedExpAgingReport(institutionId);
            setReport(data);
        } catch (e) {
            setReport(null);
            toast.showApiError(e, 'Failed to load unrealized-EXP aging report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const blob = await CommercialReportService.downloadUnrealizedExpAgingReportXlsx(institutionId);
            triggerDownload(blob, `unrealized-exp-aging-${toLocalIsoDate()}.xlsx`);
        } catch (e) {
            toast.showApiError(e, 'Download failed');
        } finally {
            setDownloading(false);
        }
    };

    const bucketsByGroup = (groupType: ExpAgingGroupType) => (report?.buckets ?? []).filter(b => b.groupType === groupType);
    const grandBuckets = useMemo(() => bucketsByGroup('GRAND'), [report]);
    const bankBuckets = useMemo(() => bucketsByGroup('BANK'), [report]);
    const buyerBuckets = useMemo(() => bucketsByGroup('BUYER'), [report]);

    const grandCurrencies = useMemo(() => distinctCurrencies(grandBuckets), [grandBuckets]);
    const bankCurrencies = useMemo(() => distinctCurrencies(bankBuckets), [bankBuckets]);
    const buyerCurrencies = useMemo(() => distinctCurrencies(buyerBuckets), [buyerBuckets]);

    return (
        <Box data-help-page="commercialReports.expAging.page">
            <Box data-help-section="commercialReports.expAging.toolbar" display="flex" gap={2} alignItems="flex-end" mb={2} flexWrap="wrap">
                <Button data-help="commercialReports.expAging.refresh" variant="outlined" size="small" onClick={load} disabled={loading}>Refresh</Button>
                <Button data-help="commercialReports.expAging.download"
                    variant="contained" size="small" startIcon={<FileDownloadIcon />}
                    onClick={handleDownload} disabled={!report || downloading}
                >
                    Download XLSX
                </Button>
            </Box>

            {loading && <CircularProgress size={24} />}

            {!loading && report && (
                <>
                    {/* §4.7: always rendered, never suppressed. */}
                    <Alert severity="info" sx={{ mb: 2 }} data-testid="exp-aging-buyer-caveat">
                        {report.buyerGroupingCaveat}
                    </Alert>

                    {report.truncated && (
                        <Alert severity="warning" sx={{ mb: 2 }} data-testid="exp-aging-truncated">
                            Showing {report.rows.length} of {report.totalMatchingRows} matching EXP forms. Buckets
                            below still reflect the full, untruncated dataset.
                        </Alert>
                    )}

                    <Typography variant="h6" sx={{ mb: 1 }}>Grand Total</Typography>
                    {grandCurrencies.length === 0 && <Typography color="text.secondary" sx={{ mb: 2 }}>No data.</Typography>}
                    {grandCurrencies.map(currency => (
                        <BucketTable
                            key={`grand-${currency}`} title="Grand Total" currency={currency} groupLabel="Group"
                            testIdPrefix="exp-aging-grand" buckets={grandBuckets.filter(b => b.currencyCode === currency)}
                        />
                    ))}

                    <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>By Bank</Typography>
                    {bankCurrencies.length === 0 && <Typography color="text.secondary" sx={{ mb: 2 }}>No data.</Typography>}
                    {bankCurrencies.map(currency => (
                        <BucketTable
                            key={`bank-${currency}`} title="By Bank" currency={currency} groupLabel="Bank"
                            testIdPrefix="exp-aging-by-bank" buckets={bankBuckets.filter(b => b.currencyCode === currency)}
                        />
                    ))}

                    <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>By Buyer</Typography>
                    {buyerCurrencies.length === 0 && <Typography color="text.secondary" sx={{ mb: 2 }}>No data.</Typography>}
                    {buyerCurrencies.map(currency => (
                        <BucketTable
                            key={`buyer-${currency}`} title="By Buyer" currency={currency} groupLabel="Buyer"
                            testIdPrefix="exp-aging-by-buyer" buckets={buyerBuckets.filter(b => b.currencyCode === currency)}
                        />
                    ))}

                    <Typography variant="h6" sx={{ mb: 1, mt: 2 }}>EXP Forms</Typography>
                    <TableContainer data-help-section="commercialReports.expAging.table" component={Paper} data-testid="exp-aging-rows-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.expAging.col.expNumber">EXP Number</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.buyer">Buyer</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.bank">Bank</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.currency">Currency</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.invoice" align="right">Invoice Value</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.realized" align="right">Realized</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.unrealized" align="right">Unrealized</TableCell>
                                    <TableCell data-help="commercialReports.expAging.col.bucket">Bucket</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.rows.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="text.secondary">No unrealized EXP forms.</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {report.rows.map(row => (
                                    <TableRow key={row.expId} hover>
                                        <TableCell>{row.expNumber}</TableCell>
                                        <TableCell>{row.buyerName}</TableCell>
                                        <TableCell>{row.bankName}</TableCell>
                                        <TableCell>{row.currencyCode}</TableCell>
                                        <TableCell align="right">{money(row.invoiceValue)}</TableCell>
                                        <TableCell align="right">{money(row.realizedAmount)}</TableCell>
                                        <TableCell align="right">{money(row.unrealizedAmount)}</TableCell>
                                        <TableCell>{row.bucketLabel}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {!loading && !report && (
                <Typography color="text.secondary">No data available.</Typography>
            )}
        </Box>
    );
};

export default UnrealizedExpAgingReport;
